import type { Board } from "../board";
import { DamageType } from "../damageType";
import { ParticleEffect } from "../effects/particleEffect";
import { PRIORITY_DEFAULT, type ParticleEmitter, type ProcessableThing } from "../interfaces";
import { ParticleType } from "../particleType";
import { STD_BASE_HP } from "../physicalThingWithHp";
import { Slugant } from "../mobs/slugant";
import { deserializeXY } from "../serialization";
import { Sound } from "./sounds/sound";
import { SoundId } from "./sounds/soundId";
import type { Thing } from "../thing";
import type { XY } from "../xy";
import { SolidifiedMob } from "./solidifiedMob";

export class SolidifiedSlugant extends SolidifiedMob implements ProcessableThing, ParticleEmitter {
	static key = "SolidifiedSlugant";
	static DEFAULT_TURNS = 3;

	turnsRemaining: number;
	gold: number;

	constructor(xy: XY, turnsRemaining: number = SolidifiedSlugant.DEFAULT_TURNS, gold: number = 0) {
		super(xy);
		this.turnsRemaining = turnsRemaining;
		this.gold = gold;
	}

	getProcessPriority(): number {
		return PRIORITY_DEFAULT;
	}

	processTurn(board: Board): void {
		this.turnsRemaining--;
		if (this.turnsRemaining > 0) return;

		if (!this.canRespawnSlugantHere()) {
			this.turnsRemaining = 1;
			return;
		}

		const xy = this.xy;
		const gold = this.gold;
		board.addEffect(new ParticleEffect(xy, ParticleType.SOLIDIFY_MOB_BURST, 16));
		board.addNonPhysicalThing(new Sound(SoundId.SOLIDIFIED_MOB_EMERGE, xy));
		board.removeAtEndOfTurn(this);
		board.runLater(() => {
			const slugant = new Slugant(xy);
			slugant.gold = gold;
			board.addThing(slugant);
		});
	}

	getParticleType(): ParticleType | null {
		return ParticleType.SOLIDIFY_MOB_BURST;
	}

	getParticleIntensity(): number {
		return 0.15;
	}

	protected onZeroHp(source: Thing | null, type: DamageType | null): void {
		super.onZeroHp(source, type);
		if (!this.board) return;
		const proxy = new Slugant(this.xy);
		proxy.gold = this.gold;
		this.board.addThing(proxy);
		const killType = type === DamageType.BUBBLE ? DamageType.MELEE : type;
		proxy.applyDamage(killType, STD_BASE_HP * 100, source);
	}

	private canRespawnSlugantHere(): boolean {
		if (!this.board) return false;
		if (this.board.hasMob(this.xy)) return false;
		return this.board.getThingsAt(this.xy).every((t) => t === this || !t.blocksMovement);
	}

	serialize(): Record<string, unknown> {
		const data: Record<string, unknown> = {
			...super.serialize(),
			turnsRemaining: this.turnsRemaining,
		};
		if (this.gold > 0) data.gold = this.gold;
		return data;
	}

	static deserialize(data: any): SolidifiedSlugant {
		const c = new SolidifiedSlugant(
			deserializeXY(data.xy),
			data.turnsRemaining ?? SolidifiedSlugant.DEFAULT_TURNS,
			data.gold ?? 0,
		);
		c.restoreBaseFields(data);
		return c;
	}
}
